import { Texture } from "pixi.js"

/**
 * Bare wall edges: a piece drawn as if a wall went on past its cell (a raised wall shaped by the storey below, a run
 * cut short) stops at the cell's edge with nothing to meet it. Each such pixel is closed with the tile's own outline
 * colour, the line the art draws wherever a wall really ends. One pixel read per tile texture, one bake per tile and
 * set of bare edges, none in the frame loop.
 */

export type WallSide = "north" | "east" | "south" | "west"
export const WALL_SIDES: WallSide[] = ["north", "east", "south", "west"]
const FACING: Record<WallSide, WallSide> = { north: "south", east: "west", south: "north", west: "east" }

/** What a tile shows along each edge: where it has art, and where that art is not already an outline. */
export interface EdgeProfile {
    size: number
    opaque: Record<WallSide, boolean[]>
    open: Record<WallSide, boolean[]>
    /** The colour this tile outlines itself with (its darkest pixel), as [r, g, b]. */
    outline: [number, number, number]
}

/** A pixel this dark is an outline already (timber's is 43,28,29; the fence sheets' are darker still). */
const OUTLINE_MAX = 72
const DEFAULT_OUTLINE: [number, number, number] = [43, 28, 29]

/** A tile's edges from its RGBA pixels, square `size` x `size`. */
export const edgeProfileOf = (data: ArrayLike<number>, size: number): EdgeProfile => {
    let outline = DEFAULT_OUTLINE, darkest = Infinity
    for (let i = 0; i < size * size * 4; i += 4) {
        if (data[i + 3] < 128) continue
        const sum = data[i] + data[i + 1] + data[i + 2]
        if (sum < darkest && Math.max(data[i], data[i + 1], data[i + 2]) <= OUTLINE_MAX) {
            darkest = sum
            outline = [data[i], data[i + 1], data[i + 2]]
        }
    }
    const at = (x: number, y: number) => (y * size + x) * 4
    const along = (side: WallSide, k: number) =>
        side === "north" ? at(k, 0) : side === "south" ? at(k, size - 1) : side === "west" ? at(0, k) : at(size - 1, k)
    const opaque = {} as Record<WallSide, boolean[]>, open = {} as Record<WallSide, boolean[]>
    WALL_SIDES.forEach(side => {
        opaque[side] = []
        open[side] = []
        for (let k = 0; k < size; k++) {
            const i = along(side, k)
            const solid = data[i + 3] >= 128
            opaque[side].push(solid)
            open[side].push(solid && Math.max(data[i], data[i + 1], data[i + 2]) > OUTLINE_MAX)
        }
    })
    return { size, opaque, open, outline }
}

/**
 * Where a tile's edges meet nothing: art that is not an outline, on an edge the tile beside it has no art on,
 * pixel for pixel. `neighbours` are the tiles in the cells beside it (none: an empty cell).
 */
export const bareEdges = (own: EdgeProfile, neighbours: Partial<Record<WallSide, EdgeProfile>>): Record<WallSide, number[]> => {
    const bare = {} as Record<WallSide, number[]>
    WALL_SIDES.forEach(side => {
        const facing = neighbours[side]?.opaque[FACING[side]]
        bare[side] = []
        own.open[side].forEach((open, k) => {
            if (open && !facing?.[k]) bare[side].push(k)
        })
    })
    return bare
}

export const hasBareEdges = (bare: Record<WallSide, number[]>) => WALL_SIDES.some(side => bare[side].length)

/** The pixels of a texture's frame, drawn onto a canvas of its size; undefined when it has no image to read. */
const drawFrame = (texture: Texture): { canvas: HTMLCanvasElement; context: CanvasRenderingContext2D } | undefined => {
    const source = (texture.baseTexture.resource as any)?.source
    if (!source) return undefined
    const canvas = document.createElement("canvas")
    canvas.width = texture.width
    canvas.height = texture.height
    const context = canvas.getContext("2d")
    if (!context) return undefined
    const frame = texture.frame, trim = texture.trim
    context.drawImage(source, frame.x, frame.y, frame.width, frame.height,
        trim?.x || 0, trim?.y || 0, trim?.width || texture.width, trim?.height || texture.height)
    return { canvas, context }
}

const profiles = new WeakMap<Texture, EdgeProfile | null>()

/** A tile texture's edges, read once. Undefined for a texture that is not a square tile or has no image yet. */
export const edgeProfile = (texture: Texture | undefined): EdgeProfile | undefined => {
    if (!texture || texture.width !== texture.height || !texture.baseTexture.valid) return undefined
    const known = profiles.get(texture)
    if (known !== undefined) return known ?? undefined
    let profile: EdgeProfile | null = null
    try {
        const drawn = drawFrame(texture)
        if (drawn) profile = edgeProfileOf(drawn.context.getImageData(0, 0, texture.width, texture.height).data, texture.width)
    } catch {
        profile = null
    }
    profiles.set(texture, profile)
    return profile ?? undefined
}

const baked = new WeakMap<Texture, Map<string, Texture>>()

/** `texture` with its bare edges closed in `outline`, baked once per texture and set of bare pixels. */
export const withBareEdges = (texture: Texture, bare: Record<WallSide, number[]>, outline: [number, number, number]): Texture => {
    if (!hasBareEdges(bare)) return texture
    const key = WALL_SIDES.map(side => bare[side].join(".")).join("|")
    const forTexture = baked.get(texture) ?? new Map<string, Texture>()
    baked.set(texture, forTexture)
    const cached = forTexture.get(key)
    if (cached && !cached.baseTexture.destroyed) return cached
    const drawn = drawFrame(texture)
    if (!drawn) return texture
    const { canvas, context } = drawn
    const size = texture.width
    context.fillStyle = `rgb(${outline[0]}, ${outline[1]}, ${outline[2]})`
    bare.north.forEach(k => context.fillRect(k, 0, 1, 1))
    bare.south.forEach(k => context.fillRect(k, size - 1, 1, 1))
    bare.west.forEach(k => context.fillRect(0, k, 1, 1))
    bare.east.forEach(k => context.fillRect(size - 1, k, 1, 1))
    // A canvas-backed texture, like the grass fringe's: the damage system can still read its image.
    const result = Texture.from(canvas)
    forTexture.set(key, result)
    return result
}
